import type { FC, ReactElement } from 'react';
import { useEffect } from 'react';

import { crossOriginCommunicationController } from '../../../controllers/crossOriginCommunication/CrossOriginCommunicationControllerConnector';
import { OutgoingMessage } from '../../../controllers/crossOriginCommunication/types';
import { useRedirectToLobbyCallbackConnected } from '../../../hooks/useRdirectToLobbyCallbackConnected';
import { useCoreDispatch } from '../../../store/CoreStore';
import { useConfigSlice } from '../../../store/slices/config/selectors';
import { configActions } from '../../../store/slices/config/slice';
import { headerActions } from '../../../store/slices/header/slice';
import { HeaderModal } from '../../../store/slices/header/types';
import { popupActions } from '../../../store/slices/popup/slice';
import { ButtonVariant } from '../../controls/button/Button';
import { PopupType } from '../../popup/types';
import { L12nText } from '../../text/L12nText';

import { BlockingModalActionButton, BlockingModalContent } from './BlockingModalContent';

export const RealityCheckConnected: FC = (): ReactElement => {
  const dispatch = useCoreDispatch();

  const baseUrl = useConfigSlice.baseUrl();
  const realityCheck = useConfigSlice.realityCheck();
  const tableId = useConfigSlice.session()?.tableId as string;
  const redirectToLobby = useRedirectToLobbyCallbackConnected();

  useEffect(() => {
    crossOriginCommunicationController.dispatch(OutgoingMessage.RealityCheckShown);
  }, []);

  const url = `${baseUrl}/api/cgibin/regulation/realityCheck`;

  const action: BlockingModalActionButton[] = [
    {
      tk: 'Continue',
      type: ButtonVariant.ACCENT,
      click: (): void => {
        // Do not wait for response
        void fetch(url, {
          method: 'POST',
          body: JSON.stringify({ userAction: 'RESUME', tableId: tableId }),
        });
        crossOriginCommunicationController.dispatch(OutgoingMessage.RealityCheckContinue);
        dispatch(configActions.setRealityCheck(null));
        dispatch(popupActions.dismissPopup(PopupType.RealityCheck));
      },
    },
    {
      tk: 'History',
      type: ButtonVariant.MAIN,
      click: (): void => {
        // Do not wait for response
        void fetch(url, {
          method: 'POST',
          body: JSON.stringify({ userAction: 'RESUME', tableId: tableId }),
        });
        dispatch(headerActions.setOpenedModal(HeaderModal.History));
        dispatch(configActions.setRealityCheck(null));
        dispatch(popupActions.dismissPopup(PopupType.RealityCheck));
      },
    },
    {
      tk: 'Quit',
      type: ButtonVariant.MAIN,
      click: (): void => {
        crossOriginCommunicationController.dispatch(OutgoingMessage.RealityCheckQuit);
        fetch(url, {
          method: 'POST',
          body: JSON.stringify({ userAction: 'STOP', tableId: tableId }),
        }).then(() => redirectToLobby());
      },
    },
  ];
  const component = (
    <L12nText tk="REALITY_CHECK_MESSAGE" dynamicValues={[realityCheck!.interval, realityCheck!.passed]} />
  );

  return <BlockingModalContent titleTk="GAME_CARE" messageComponent={component} actions={action} />;
};
